/**
 * Prioritize "More from this shop" by the destination/theme encoded in the
 * current product title. Shopify's native recommendations fill any remaining
 * cards only after same-subject products have been shown.
 */
(() => {
  const MAX_PRODUCTS = 8;

  const subjectFromTitle = (title) => {
    const firstClause = String(title || '').split('|')[0].trim();
    return firstClause
      .replace(/\s+(?:coffee\s+)?(?:latte\s+)?mug\b.*$/i, '')
      .replace(/\s+(?:travel\s+)?tumbler\b.*$/i, '')
      .replace(/\s+(?:accent\s+)?(?:ceramic\s+)?cup\b.*$/i, '')
      .replace(/\s+(?:illustrated\s+)?(?:travel\s+)?(?:souvenir\s+)?gift\b.*$/i, '')
      .trim();
  };

  // Search suggest returns decimal strings ("19.19"), while Shopify's
  // recommendations endpoint returns integer cents (1919). Normalize both
  // API shapes before formatting so a $19.19 mug never renders as $1,919.
  const money = (value) => {
    const raw = String(value ?? '').trim();
    const numeric = Number(raw);
    const amount = /^\d+$/.test(raw) ? numeric / 100 : numeric;
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency', currency: window.Shopify?.currency?.active || 'USD',
    }).format(amount);
  };

  const card = (product) => {
    // The same two Shopify endpoints use different featured_image shapes:
    // search suggest supplies an object, recommendations supplies a URL string.
    const featuredImage = product.featured_image;
    const image = typeof featuredImage === 'string'
      ? featuredImage
      : featuredImage?.url || product.image || product.featured_media?.preview_image?.src || '';
    const imageAlt = (typeof featuredImage === 'object' ? featuredImage?.alt : '') || product.title || '';
    const price = product.price ?? product.price_min;
    const compareAt = product.compare_at_price ?? product.compare_at_price_max;
    const onSale = Number(compareAt) > Number(price);
    return `<a class="etsy-under-media-card" href="${product.url}" aria-label="${escapeHtml(product.title)}">
      <span class="etsy-under-media-card__media">${image ? `<img src="${image}" alt="${escapeHtml(imageAlt)}" loading="lazy" width="360" height="360">` : ''}</span>
      <span class="etsy-under-media-card__price"><strong>${money(price)}</strong>${onSale ? ` <s>${money(compareAt)}</s>` : ''}</span>
    </a>`;
  };

  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  }[char]));

  const fetchJson = async (url) => {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return [];
    return response.json();
  };

  const load = async (module) => {
    const productId = module.dataset.productId;
    const currentHandle = module.dataset.productHandle;
    const subject = subjectFromTitle(module.dataset.productTitle);
    if (!productId || !currentHandle || !subject || subject.length < 2) return;

    const sameSubject = await fetchJson(
      `/search/suggest.json?q=${encodeURIComponent(subject)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last`
    );
    const selected = [];
    const addUnique = (products) => {
      for (const product of products || []) {
        if (product.handle === currentHandle || selected.some((item) => item.handle === product.handle)) continue;
        selected.push(product);
        if (selected.length >= MAX_PRODUCTS) break;
      }
    };
    addUnique(sameSubject.resources?.results?.products);

    if (selected.length < MAX_PRODUCTS) {
      const fallback = await fetchJson(`/recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${MAX_PRODUCTS}`);
      addUnique(fallback.products);
    }

    if (selected.length) {
      const grid = module.querySelector('.etsy-under-media-recommendations__grid');
      if (grid) grid.innerHTML = selected.slice(0, MAX_PRODUCTS).map(card).join('');
      module.dataset.recommendationSubject = subject;
    }
  };

  const initialize = () => document
    .querySelectorAll('[data-prioritized-shop-recommendations]')
    .forEach((module) => load(module).catch(() => {}));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
